import React from "react";
import { cn } from "@/lib/utils";
import { VLKRiskMeter } from "@/components/vlk";

/* ------------------------------------------------------------------ */
/*  Severity + category color tokens (VLK design language)            */
/* ------------------------------------------------------------------ */

export const SEVERITY_ORDER = ["low", "medium", "high", "critical"];

export const SEVERITY_STYLES = {
  low:      { dot: "bg-info",       chip: "text-muted-foreground border-border" },
  medium:   { dot: "bg-warning",    chip: "text-warning border-warning/30" },
  high:     { dot: "bg-destructive/80", chip: "text-destructive border-destructive/30" },
  critical: { dot: "bg-destructive",   chip: "text-destructive border-destructive font-semibold" },
};

export const CATEGORY_LABELS = {
  identity:        "Identity",
  identity_lock:   "Lock",
  identity_unlock: "Unlock",
  recovery:        "Recovery",
  developer:       "Developer",
  institution:     "Institution",
  auth:            "Authentication",
  qr:              "QR Verify",
  system:          "System",
};

export function categoryOptions() {
  return Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }));
}

/* ------------------------------------------------------------------ */
/*  Client-side filtering                                             */
/* ------------------------------------------------------------------ */

export function applyFilters(events, filters) {
  if (!filters) return events;
  const search = (filters.search || "").trim().toLowerCase();
  const sev = filters.severity || "";
  const cat = filters.category || "";
  const actor = (filters.actor || "").trim().toLowerCase();
  const inst = (filters.institution || "").trim().toLowerCase();

  return events.filter((e) => {
    if (sev && e.risk_level !== sev) return false;
    if (cat && e.category !== cat) return false;
    if (actor && !(e.actor_email || "").toLowerCase().includes(actor)) return false;
    if (inst && !(e.institution_id || "").toLowerCase().includes(inst)) return false;
    if (search) {
      const hay = [
        e.action, e.actor_email, e.target_id, e.category,
        e.correlation_id, e.role, e.outcome, e.institution_id, e.case_id,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

/* ------------------------------------------------------------------ */
/*  Row renderer (used by console + full page)                         */
/* ------------------------------------------------------------------ */

export function severityDot(level) {
  const sev = SEVERITY_STYLES[level] || SEVERITY_STYLES.low;
  return <span className={cn("inline-block h-1.5 w-1.5 rounded-full", sev.dot)} aria-label={`severity ${level}`} />;
}

export function severityChip(level) {
  const sev = SEVERITY_STYLES[level] || SEVERITY_STYLES.low;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-1.5 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider font-mono", sev.chip)}>
      {severityDot(level)}{level}
    </span>
  );
}

export function outcomeText(outcome) {
  const ok = outcome === "SUCCESS";
  return (
    <span className={cn("text-[10px] uppercase tracking-wider font-mono", ok ? "text-success" : "text-destructive")}>
      {outcome}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  CSV / JSON export                                                 */
/* ------------------------------------------------------------------ */

export function eventsToCsv(events) {
  const fields = [
    "timestamp", "risk_level", "category", "action", "outcome",
    "actor_email", "role", "target_type", "target_id",
    "institution_id", "case_id", "correlation_id", "integrity_hash", "ledger_id",
  ];
  const header = fields.join(",");
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = events.map((e) => fields.map((f) => escape(e[f])).join(","));
  return [header, ...lines].join("\n");
}

export function downloadBlob(filename, mime, contents) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/*  Human-readable timestamp                                          */
/* ------------------------------------------------------------------ */

export function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function formatDateTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString();
}

/* ------------------------------------------------------------------ */
/*  SLA breach chip (cases)                                           */
/* ------------------------------------------------------------------ */

const BREACH_STYLES = {
  ok:        { chip: "border-border text-muted-foreground", label: "SLA on track" },
  warning:   { chip: "border-warning/40 text-warning bg-warning/5", label: "SLA warning" },
  imminent:  { chip: "border-destructive/40 text-destructive bg-destructive/5", label: "SLA imminent" },
  breached:  { chip: "border-destructive text-destructive bg-destructive/10 font-semibold", label: "SLA breached" },
};

export function formatRemaining(secs) {
  if (secs == null) return "—";
  const neg = secs < 0;
  const s = Math.abs(secs);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return (neg ? "-" : "") + parts.join(" ");
}

export function SlaChip({ status }) {
  if (!status) return null;
  const key = status.breach_level || "ok";
  const st = BREACH_STYLES[key] || BREACH_STYLES.ok;
  return (
    <span data-testid={`sla-chip-${key}`}
          className={cn("inline-flex items-center gap-1.5 px-1.5 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider font-mono", st.chip)}>
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
      {formatRemaining(status.remaining_seconds)}
    </span>
  );
}
